import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useScene } from '../../hooks';
import { TextButton } from '../generic/TextButton';

enum Actions {
  Install,
  CompleteInstallTask,
  Uninstall,
  CompleteUninstallTask,
  ReleaseUpdate,
  Update,
  CompleteUpdateTask,
  CorruptFiles,
  Repair,
  CompleteRepairTask,
}

export const FlatStatesControlPanel = () => {
  const scene = useScene();
  const [sceneState, setSceneState] = useState<{
    name: string;
    availableActions: Actions[];
  } | null>(null);

  const setLauncherState = (state: string, value: boolean) => {
    scene.setLauncherStates((states) => ({
      ...states,
      [`${state}`]: value,
    }));
  };

  const renderButton = (action: Actions, key?: string) => {
    switch (action) {
      case Actions.Install:
        return (
          <div key={key}>
            <TextButton
              type="info"
              handleClick={() => {
                setLauncherState('doesAppOngoingTaskExist', true);
                setLauncherState('doesAppOngoingInstallTaskExist', true);
              }}
            >
              Install
            </TextButton>
          </div>
        );
      case Actions.CompleteInstallTask:
        return (
          <div key={key}>
            <TextButton
              type="info"
              handleClick={() => {
                setLauncherState('doesAppOngoingTaskExist', false);
                setLauncherState('doesAppOngoingInstallTaskExist', false);
                setLauncherState('isAppInstalled', true);
              }}
            >
              Complete Install Task
            </TextButton>
          </div>
        );
      case Actions.Uninstall:
        return (
          <div key={key}>
            <TextButton
              type="error"
              handleClick={() => {
                setLauncherState('doesAppOngoingTaskExist', true);
                setLauncherState('doesAppOngoingUninstallTaskExist', true);
              }}
            >
              Uninstall
            </TextButton>
          </div>
        );
      case Actions.CompleteUninstallTask:
        return (
          <div key={key}>
            <TextButton
              type="error"
              handleClick={() =>
                scene.setLauncherStates({
                  doesAppDirExist: false,
                  isAppInstalled: false,
                  doesAppNeedRepair: false,
                  isAppUpdateAvailable: false,
                  doesAppOngoingTaskExist: false,
                  doesAppOngoingInstallTaskExist: false,
                  doesAppOngoingUpdateTaskExist: false,
                  doesAppOngoingUninstallTaskExist: false,
                  doesAppOngoingRepairTaskExist: false,
                  doesAppOngoingMoveTaskExist: false,
                  doesAppLastTaskFailed: false,
                })
              }
            >
              Complete Uninstall Task
            </TextButton>
          </div>
        );
      case Actions.ReleaseUpdate:
        return (
          <div key={key}>
            <TextButton
              handleClick={() => {
                setLauncherState('isAppUpdateAvailable', true);
              }}
            >
              Release Update
            </TextButton>
          </div>
        );
      case Actions.Update:
        return (
          <div key={key}>
            <TextButton
              type="success"
              handleClick={() => {
                setLauncherState('doesAppOngoingTaskExist', true);
                setLauncherState('doesAppOngoingUpdateTaskExist', true);
              }}
            >
              Update
            </TextButton>
          </div>
        );
      case Actions.CompleteUpdateTask:
        return (
          <div key={key}>
            <TextButton
              type="success"
              handleClick={() => {
                setLauncherState('doesAppOngoingTaskExist', false);
                setLauncherState('doesAppOngoingUpdateTaskExist', false);
                setLauncherState('isAppUpdateAvailable', false);
              }}
            >
              Complete Update Task
            </TextButton>
          </div>
        );
      case Actions.CorruptFiles:
        return (
          <div key={key}>
            <TextButton
              handleClick={() => {
                setLauncherState('doesAppNeedRepair', true);
              }}
            >
              Corrupt Files
            </TextButton>
          </div>
        );
      case Actions.Repair:
        return (
          <div key={key}>
            <TextButton
              type="warn"
              handleClick={() => {
                setLauncherState('doesAppOngoingTaskExist', true);
                setLauncherState('doesAppOngoingRepairTaskExist', true);
              }}
            >
              Repair
            </TextButton>
          </div>
        );
      case Actions.CompleteRepairTask:
        return (
          <div key={key}>
            <TextButton
              type="warn"
              handleClick={() => {
                setLauncherState('doesAppOngoingTaskExist', false);
                setLauncherState('doesAppOngoingRepairTaskExist', false);
                setLauncherState('doesAppNeedRepair', false);
              }}
            >
              Compete Repair Task
            </TextButton>
          </div>
        );
      default:
        return <div key={key} />;
    }
  };

  useEffect(() => {
    const states = scene.launcherStates;
    let stateName = 'App is Not Installed';
    let availableActions = [Actions.Install];

    if (states.isAppInstalled === true) {
      stateName = 'App is Installed';
      availableActions = [
        Actions.CorruptFiles,
        Actions.ReleaseUpdate,
        Actions.Uninstall,
      ];
    }
    if (states.doesAppNeedRepair === true) {
      stateName = 'App Need Repair';
      availableActions = [Actions.Repair, Actions.Uninstall];
    }
    if (states.isAppUpdateAvailable === true) {
      stateName = 'App Update is Available';
      availableActions = [Actions.Update, Actions.Uninstall];
    }

    if (states.doesAppOngoingInstallTaskExist === true) {
      stateName = 'App is Installing';
      availableActions = [Actions.CompleteInstallTask];
    }
    if (states.doesAppOngoingUninstallTaskExist === true) {
      stateName = 'App is Uninstalling';
      availableActions = [Actions.CompleteUninstallTask];
    }
    if (states.doesAppOngoingUpdateTaskExist === true) {
      stateName = 'App is Updating';
      availableActions = [Actions.CompleteUpdateTask];
    }
    if (states.doesAppOngoingRepairTaskExist === true) {
      stateName = 'App is Repairing';
      availableActions = [Actions.CompleteRepairTask];
    }

    setSceneState({
      name: stateName,
      availableActions,
    });
  }, [scene.launcherStates]);

  return (
    <StateContainer>
      <Separator />
      <StateName>{sceneState?.name}</StateName>
      <Separator />

      {sceneState?.availableActions.map((action, i) =>
        renderButton(action, `action-button-${i}`)
      )}
    </StateContainer>
  );
};

const StateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StateName = styled.span`
  font-size: 14px;
  color: var(--accent-color);
  text-align: center;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  margin: 10px 0;
  background: linear-gradient(
    90deg,
    transparent,
    var(--input-border-color) 10%,
    var(--input-border-color) 90%,
    transparent
  );
`;
